<template>
  <v-card class="tax-summary-profit-by-position px-4 pt-2 pb-4 my-2 elevation-0">
    <v-card-title class="px-1 pb-3">
      <h1 class="card-title mb-1">
        {{ $t(`views.tax.taxSummary.profitByPosition.title`) }}
      </h1>
      <v-spacer />
      <ExportButton :click="downloadCsv" :loading="exportLoading" />
    </v-card-title>
    <v-card-text class="mt-0 pa-0">
      <v-data-table
        :headers="headers"
        :items="yearSummary.profitsByPosition"
        class="elevation-0"
        mobile-breakpoint="0"
        :items-per-page="10"
        :page.sync="currentPage"
        :item-class="itemRowBackground"
        :dense="user.preferences.denseTables"
        :loading="loading"
      >
        <!-- <template slot="body.prepend">
            <tr>
              <td class="text-right">
                <span />
              </td>
              <td>
                <div class="items-flex">
                  TOTAL
                  <div v-if="somePendingRecreatePosition" class="ml-3">
                    <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.textTotal')" />
                  </div>
                </div>
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.totalReceivedWithComissionBaseCurrency"
                  :currency="user.baseCurrency"
                />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.totalCostWithComissionBaseCurrency"
                  :currency="user.baseCurrency"
                />
              </td>
              <td class="text-right">
                <span />
              </td>
              <td class="text-right">
                <currency
                  :value="totalRow.profitWithComissionBaseCurrency || 0"
                  :currency="user.baseCurrency"
                  :text-green="totalRow.profitWithComissionBaseCurrency > 0"
                  :text-red="totalRow.profitWithComissionBaseCurrency < 0"
                  :negative="totalRow.profitWithComissionBaseCurrency < 0"
                  strong
                />
              </td>
            </tr>
          </template> -->

        <template v-slot:item.ticker="{ item }">
          <div class="items-flex">
            <TableTicker
              :position="{
                ticker: item.ticker,
                country: item.country,
                type: item.type,
              }"
              notLinkToDetail
            />
            <template v-if="item.someIncompleteExchangeRate">
              <div class="ml-3 mt-1">
                <WarningIcon :text="$t(`views.dashboardDetail.orders.ordersByPosition.warning`)" />
              </div>
            </template>
          </div>
        </template>
        <template v-slot:item.type="{ item }">
          <div class="items-flex">
            {{ $t(`common.positionTypes.${item.type}`) }}
          </div>
        </template>
        <template v-slot:item.transmision="{ item }">
          <currency
            v-if="item.transmision"
            :value="item.transmision"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.adquisicion="{ item }">
          <currency
            v-if="item.adquisicion"
            :value="item.adquisicion"
            :currency="user.baseCurrency"
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.profit="{ item }">
          <currency
            v-if="item.profit"
            :value="item.profit"
            :currency="user.baseCurrency"
            text-green
            strong
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.loss="{ item }">
          <currency
            v-if="item.loss"
            :value="item.loss"
            :currency="user.baseCurrency"
            text-red
            strong
            :hideDataNoPlanUser="hideDataNoPlanUser"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.destinationRetention="{ item }">
          <currency
            v-if="item.destinationRetention"
            :value="item.destinationRetention"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { getTaxSummaryCsv } from '@/lib/api.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { Currency, TableTicker, WarningIcon, ExportButton },
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    yearSelected: {
      type: String,
      required: true,
    },
    yearSummary: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: null,
      exportLoading: false,
    }
  },
  computed: {
    ...mapState(['user']),
    headers() {
      return [
        {
          text: this.$t(`views.tax.taxSummary.profitByPosition.ticker`),
          value: 'ticker',
        },
        {
          text: this.$t(`views.tax.taxSummary.profitByPosition.type`),
          value: 'type',
        },
        {
          text: `${this.$t(
            'views.tax.taxSummary.profitByPosition.adquisicion'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'adquisicion',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.tax.taxSummary.profitByPosition.transmision'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'transmision',
          align: 'right',
        },
        {
          text: this.$t('views.tax.taxSummary.profitByPosition.profit'),
          value: 'profit',
          align: 'right',
        },
        {
          text: this.$t('views.tax.taxSummary.profitByPosition.loss'),
          value: 'loss',
          align: 'right',
        },
        {
          text: this.$t('views.tax.taxSummary.profitByPosition.destinationRetention'),
          value: 'destinationRetention',
          align: 'right',
        },
      ]
    },
  },
  methods: {
    itemRowBackground(item) {
      if (item.someIncompleteExchangeRate) return 'row-advise'
    },
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await getTaxSummaryCsv({
          startDate: this.startDate,
          endDate: this.endDate,
          section: 'profitsByPosition',
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        const title = `${this.$t('document.title.tax')} ${this.yearSelected}. ${this.$t(
          `views.tax.taxSummary.profitByPosition.title`
        )}`
        FileSaver.saveAs(blob, `Filios - ${title} - ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss"></style>
